import { useState, useEffect } from 'react';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface ImpactType {
  id: string;
  name: string;
  label: string;
  unit: string;
  icon: string;
  color: string;
  bg_color: string;
  value: number;
  active: boolean;
  order_index: number;
  created_at?: string;
  updated_at?: string;
}

const defaultImpactTypes: ImpactType[] = [
  {
    id: 'co2_saved',
    name: 'co2_saved',
    label: 'CO₂ Saved',
    unit: 'kg',
    icon: 'Leaf',
    color: 'text-green-600',
    bg_color: 'bg-green-100',
    value: 12847.5,
    active: true,
    order_index: 1
  },
  {
    id: 'waste_reduced',
    name: 'waste_reduced',
    label: 'Waste Reduced',
    unit: 'kg',
    icon: 'Trash2',
    color: 'text-orange-600',
    bg_color: 'bg-orange-100',
    value: 8924.2,
    active: true,
    order_index: 2
  },
  {
    id: 'trees_planted',
    name: 'trees_planted',
    label: 'Trees Planted',
    unit: '',
    icon: 'TreePine',
    color: 'text-emerald-600',
    bg_color: 'bg-emerald-100',
    value: 1247,
    active: true,
    order_index: 3
  },
  {
    id: 'water_saved',
    name: 'water_saved',
    label: 'Water Saved',
    unit: 'L',
    icon: 'Droplets',
    color: 'text-blue-600',
    bg_color: 'bg-blue-100',
    value: 445720,
    active: true,
    order_index: 4
  },
  {
    id: 'energy_saved',
    name: 'energy_saved',
    label: 'Energy Saved',
    unit: 'kWh',
    icon: 'Zap',
    color: 'text-yellow-600',
    bg_color: 'bg-yellow-100',
    value: 9847.3,
    active: true,
    order_index: 5
  },
  {
    id: 'total_users',
    name: 'total_users',
    label: 'Total Users',
    unit: '',
    icon: 'Users',
    color: 'text-purple-600',
    bg_color: 'bg-purple-100',
    value: 50000,
    active: true,
    order_index: 6
  },
  {
    id: 'total_missions_completed',
    name: 'total_missions_completed',
    label: 'Missions Completed',
    unit: '',
    icon: 'Target',
    color: 'text-indigo-600',
    bg_color: 'bg-indigo-100',
    value: 2300000,
    active: true,
    order_index: 7
  }
];

export function useImpactTypes() {
  const [allImpactTypes, setAllImpactTypes] = useState<ImpactType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchImpactTypes();
  }, []);

  const fetchImpactTypes = async () => {
    try {
      setLoading(true);
      
      if (!isSupabaseConfigured) {
        console.log('Using default impact types data');
        setAllImpactTypes(defaultImpactTypes);
        setLoading(false);
        return;
      }

      console.log('Fetching impact types and stats from database...');
      
      // First get the impact type configurations
      const { data: typesData, error: typesError } = await supabase
        .from('impact_types')
        .select('*')
        .order('order_index', { ascending: true });

      if (typesError) {
        console.error('Error fetching impact types:', typesError);
        setAllImpactTypes(defaultImpactTypes);
        setLoading(false);
        return;
      }

      // Then get the impact stats
      const { data: statsData, error: statsError } = await supabase
        .from('impact_stats')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(1);

      if (statsError) {
        console.error('Error fetching impact stats:', statsError);
        setAllImpactTypes(defaultImpactTypes);
        setLoading(false);
        return;
      }

      const stats = statsData?.[0];
      if (!stats) {
        console.log('No impact stats found, using defaults');
        setAllImpactTypes(defaultImpactTypes);
        setLoading(false);
        return;
      }

      // If no impact type configurations exist, use defaults
      if (!typesData || typesData.length === 0) {
        console.log('No impact type configurations found, using defaults with real values');
        const typesWithValues = defaultImpactTypes.map(type => ({
          ...type,
          value: stats[type.name as keyof typeof stats] as number || 0
        }));
        setAllImpactTypes(typesWithValues);
        setLoading(false);
        return;
      }

      // Combine type configurations with actual values
      const typesWithValues = typesData.map(type => ({
        ...type,
        value: stats[type.name as keyof typeof stats] as number || 0
      }));

      setAllImpactTypes(typesWithValues);
    } catch (error) {
      console.error('Error fetching impact types:', error);
      setAllImpactTypes(defaultImpactTypes);
    } finally {
      setLoading(false);
    }
  };

  const saveImpactType = async (impactType: Omit<ImpactType, 'value'>) => {
    try {
      if (!isSupabaseConfigured) {
        console.log('Mock save impact type:', impactType);
        await fetchImpactTypes(); // Refresh data
        return { error: null };
      }

      const { error } = await supabase
        .from('impact_types')
        .upsert(impactType, { onConflict: 'name' });

      if (error) {
        console.error('Error saving impact type:', error);
        return { error };
      }

      await fetchImpactTypes(); // Refresh data
      return { error: null };
    } catch (error) {
      console.error('Error saving impact type:', error);
      return { error };
    }
  };

  const deleteImpactType = async (typeId: string) => {
    try {
      if (!isSupabaseConfigured) {
        console.log('Mock delete impact type:', typeId);
        await fetchImpactTypes(); // Refresh data
        return { error: null };
      }

      const { error } = await supabase
        .from('impact_types')
        .delete()
        .eq('id', typeId);

      if (error) {
        console.error('Error deleting impact type:', error);
        return { error };
      }

      await fetchImpactTypes(); // Refresh data
      return { error: null };
    } catch (error) {
      console.error('Error deleting impact type:', error);
      return { error };
    }
  };

  // Get only active impact types, sorted by order
  const activeImpactTypes = allImpactTypes
    .filter(type => type.active)
    .sort((a, b) => a.order_index - b.order_index);

  return {
    impactTypes: activeImpactTypes, // This now only returns ACTIVE impact types
    allImpactTypes: allImpactTypes, // This returns ALL impact types (for admin use)
    loading,
    refetch: fetchImpactTypes,
    saveImpactType,
    deleteImpactType
  };
}